import { useMemo, useState } from 'react';

import {
  SearchQuery,
  SearchQueryVariables,
  SearchV1ExternalResourceType,
  SearchV1ExternalSearchRequestFiltersInput,
  getTypeOrNull,
  useSearchQuery,
} from '@spotify/confidence-graphql';

// Extract the resource type from the SearchQuery
type SearchResponse = Extract<
  NonNullable<SearchQuery['search']>,
  { __typename: 'SearchV1ExternalSearchResponse' }
>;
type SearchHit = SearchResponse['hits'][number];
export type SearchResource = NonNullable<SearchHit['resource']>;

export interface UseSearchOptions {
  query: string;
  types: SearchV1ExternalResourceType[];
  resourceSpecificFilters?: SearchV1ExternalSearchRequestFiltersInput;
  pageSize?: number;
  skip?: boolean;
}

export interface UseSearchResult {
  results: SearchResource[];
  isEmpty: boolean;
  isLoading: boolean;
  hasNextPage: boolean;
  nextPage: () => void;
  isLoadingNextPage: boolean;
}

export const useSearch = ({
  query,
  types,
  resourceSpecificFilters,
  pageSize = 100,
  skip = false,
}: UseSearchOptions): UseSearchResult => {
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);

  const variables: SearchQueryVariables = {
    query,
    types,
    resourceSpecificFilters,
    pageSize,
  };

  const { data, loading, fetchMore, previousData } = useSearchQuery({
    variables,
    skip: skip || !query,
  });

  // Use previousData while loading to prevent flickering
  const currentData = loading && !data ? previousData : data;
  const searchResponse = getTypeOrNull(currentData?.search);

  const results = useMemo(() => {
    if (!searchResponse?.hits) {
      return [];
    }

    return searchResponse.hits
      .map(hit => hit.resource)
      .filter(
        (resource): resource is SearchResource =>
          resource !== null && resource !== undefined,
      );
  }, [searchResponse]);

  const hasNextPage = Boolean(searchResponse?.nextPageToken);

  const nextPage = async () => {
    if (hasNextPage && searchResponse?.nextPageToken) {
      setIsLoadingNextPage(true);
      try {
        await fetchMore({
          variables: {
            ...variables,
            pageToken: searchResponse.nextPageToken,
          },
          updateQuery: (prev, { fetchMoreResult }) => {
            if (!fetchMoreResult) return prev;

            const prevSearch = getTypeOrNull(prev.search);
            const newSearch = getTypeOrNull(fetchMoreResult.search);

            if (!prevSearch || !newSearch) return fetchMoreResult;

            return {
              ...fetchMoreResult,
              search: {
                ...newSearch,
                hits: [...(prevSearch.hits || []), ...(newSearch.hits || [])],
              },
            };
          },
        });
      } finally {
        setIsLoadingNextPage(false);
      }
    }
  };

  return {
    results,
    isEmpty: !loading && !isLoadingNextPage && results.length === 0,
    isLoading: loading && !previousData,
    hasNextPage,
    nextPage,
    isLoadingNextPage,
  };
};
